import { useEffect, useMemo, useState } from 'react'
import { useFormik } from 'formik'
import { useSnackbar } from 'notistack'
import { array, boolean, object, string } from 'yup'

import type { GeoBlockingTemplate } from '@img-arena/consoles-api-streaming'
import { Divider, FormControl, FormControlLabel, Radio, RadioGroup, Stack } from '@mui/material'
import { ConsoleDialog } from '@/components/ConsoleDialog'
import { InputSectionDivider } from '@/components/inputs/InputSectionDivider'
import { SelectBox } from '@/components/inputs/SelectBox'
import { ConsoleTextField } from '@/components/inputs/StyledInputComponents'
import { useDebouncedSetter } from '@/hooks/useDebouncedSetter'
import { streamingRestApi } from '@/service/streamingApi'
import { FormDialogProps } from '@/types/forms'
import { formikSaveButtonProps } from '@/utils/forms'
import { getFormError, onFieldChange } from '@/utils/forms/formik'

import { GeoBlockingTemplateCountryAutocomplete } from './GeoBlockingTemplateCountryAutocomplete'
import {
  GeoRegion,
  RegionFilterCode,
  regions,
  territories
} from './GeoBlockingTemplateForm.constants'
import { SelectedRegionsChips } from './SelectedRegionsChips'

export type GeoBlockingFormProps = FormDialogProps<
  Omit<GeoBlockingTemplate, 'geoListField'> & { geoListField: string[] }
>

export const GeoBlockingTemplateForm = ({
  onClose,
  onSubmit,
  open = false,
  defaultValues
}: GeoBlockingFormProps) => {
  const { enqueueSnackbar } = useSnackbar()

  const isEditMode = !!defaultValues?.id

  const [regionFilter, setRegionFilter] = useState<RegionFilterCode>('All')

  const formik = useFormik({
    initialValues: {
      name: defaultValues?.name || '',
      isWhiteList: defaultValues?.isWhiteList ?? false,
      selectedRegions: defaultValues?.geoListField
        ? territories.filter((country) => defaultValues.geoListField.includes(country.iso2))
        : []
    },

    validationSchema: object().shape({
      name: string().required('Template Name is required'),
      isWhiteList: boolean(),
      selectedRegions: array()
    }),

    onSubmit: async (values, { setSubmitting }) => {
      if (!values.name) return

      try {
        setSubmitting(true)

        const body = {
          name: values.name,
          isWhiteList: values.isWhiteList,
          geoListField: JSON.stringify(values.selectedRegions.map((r) => r.iso2))
        }

        if (isEditMode) {
          await streamingRestApi.endpoints.geoBlockingTemplatesEndpoints.editGeoBlockingEndpoint(
            defaultValues.id,
            body
          )
        } else {
          await streamingRestApi.endpoints.geoBlockingTemplatesEndpoints.createGeoBlockingTemplate(
            body
          )
        }

        onSubmit?.()
        enqueueSnackbar(
          `Geo Template "${values.name}" has been ${isEditMode ? 'updated' : 'created'}`,
          { variant: 'success' }
        )
        setSubmitting(false)
        handleClose()
      } catch (e) {
        enqueueSnackbar('Failed to create', { variant: 'error' })
        setSubmitting(false)
        // Throw the error again to be caught by dialog and doesn't close it
        throw e
      }
    }
  })

  const [debouncedSetName, cleanupDebounce] = useDebouncedSetter(
    (name: string) => formik.setFieldValue('name', name, false),
    200
  )

  useEffect(() => {
    return cleanupDebounce
  }, [cleanupDebounce])

  const handleClose = () => {
    cleanupDebounce()
    onClose?.()
    formik.resetForm({
      values: {
        name: '',
        isWhiteList: true,
        selectedRegions: []
      }
    })
    setRegionFilter('All')
  }

  const filteredOptions = useMemo(() => {
    if (regionFilter === 'All') return territories

    const selectedContinent = regions.find((c) => c.name === regionFilter)
    if (!selectedContinent) return territories

    return territories.filter((country) => {
      if (selectedContinent.name === 'US State' || selectedContinent.name === 'CA Province') {
        return country.continent === selectedContinent.name
      }
      return country.continent === selectedContinent.code
    })
  }, [regionFilter])

  const handleRegionChange = (newSelectedRegions: GeoRegion[]) => {
    onFieldChange(formik, 'selectedRegions', newSelectedRegions)
  }

  const handleRemoveRegion = (regionToRemove: GeoRegion) => {
    const updatedRegions = [...formik.values.selectedRegions].filter(
      (region) => region.iso2 !== regionToRemove.iso2
    )
    onFieldChange(formik, 'selectedRegions', updatedRegions)
  }

  return (
    <ConsoleDialog
      open={open}
      title={isEditMode ? 'Edit Geo Template' : 'Create Geo Template'}
      onClose={handleClose}
      dialogSize={false}
      width={733}
      smallTopMargin
      SaveButtonProps={formikSaveButtonProps(formik)}
    >
      <Stack direction="column" spacing={2}>
        <Stack direction="column">
          <ConsoleTextField
            onChange={(e) => debouncedSetName(e.target.value)}
            onBlur={() => formik.setFieldTouched('name', true, true)}
            defaultValue={defaultValues?.name || ''}
            error={getFormError(formik, 'name')}
            required
            label="Template Name"
          />
        </Stack>

        <Stack direction="row" spacing={2}>
          <Stack direction="column" width="50%">
            <InputSectionDivider label="Geo Blocking" sx={{ mt: -0.5 }} />
            <FormControl sx={{ pl: 1.25, my: 1 }}>
              <RadioGroup
                value={formik.values.isWhiteList}
                onChange={(e) => onFieldChange(formik, 'isWhiteList', e.target.value)}
              >
                <FormControlLabel
                  value={true}
                  control={<Radio size="small" sx={{ p: 0.75, mr: 0.25 }} />}
                  label="Whitelist"
                />
                <FormControlLabel
                  value={false}
                  control={<Radio size="small" sx={{ p: 0.75, mr: 0.25 }} />}
                  label="Blacklist"
                />
              </RadioGroup>
            </FormControl>

            <SelectBox
              options={regions.map((c) => c.name)}
              defaultValue="All"
              onChange={setRegionFilter}
              label="Region"
              excludeSelected
            />

            <GeoBlockingTemplateCountryAutocomplete
              options={filteredOptions}
              value={formik.values.selectedRegions}
              onChange={handleRegionChange}
            />
          </Stack>

          <Divider orientation="vertical" flexItem />

          <Stack direction="column" width="50%">
            <InputSectionDivider label="Selected Territories" sx={{ mt: -0.5, mb: 1.5 }} />

            <SelectedRegionsChips
              selectedRegions={formik.values.selectedRegions}
              regionFilter={regionFilter}
              onRemoveRegion={handleRemoveRegion}
            />
          </Stack>
        </Stack>
      </Stack>
    </ConsoleDialog>
  )
}
